import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

import type { BoardDto, Lane, Stage, TaskDto } from '@botguide/shared';
import { LANES, LANE_LABELS, STAGE_LABELS, stagesForLane } from '@botguide/shared';

import { EmptyState, LaneHeading, stageColour } from '@/components';
import { cn } from '@/lib/cn';

import { TaskCard } from './TaskCard';

/* -------------------------------------------------------------------------- */
/* The column model                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A drop target: one lane, one stage, in the order the API returned.
 *
 * The API hands back ten columns - Backlog, Next Up, seven In Progress stages
 * and Completed - and those seven stage columns are used exactly as given. The
 * only regrouping done here is splitting Completed into its two done stages,
 * which is presentational: `orderIndex` is dense within (lane, stage), so a
 * Completed column holding Pilot and Production together has two interleaved
 * orderings in it and no single position a drop could name. Splitting it means
 * every group on screen is one (lane, stage) pair, which is exactly the unit
 * `POST /tasks/:id/move` orders within.
 */
export interface BoardGroup {
  /** Droppable id. Namespaced so it can never collide with a task uuid. */
  id: string;
  lane: Lane;
  stage: Stage | null;
  label: string;
  tasks: TaskDto[];
}

export interface LaneGroups {
  lane: Lane;
  /** One entry for a lane with no stages; one per stage otherwise. */
  groups: BoardGroup[];
}

function groupId(lane: Lane, stage: Stage | null): string {
  return `group:${lane}:${stage ?? ''}`;
}

/**
 * The board as lanes of groups.
 *
 * Tasks are read out of the API's columns and filtered back by (lane, stage)
 * rather than re-derived from scratch, so the browser never decides which
 * column a card belongs in - it only decides how the columns are laid out.
 */
export function toLaneGroups(board: BoardDto): LaneGroups[] {
  const all = board.columns.flatMap((column) => column.tasks);

  return LANES.map((lane) => {
    const stages = stagesForLane(lane);

    if (stages.length === 0) {
      return {
        lane,
        groups: [
          {
            id: groupId(lane, null),
            lane,
            stage: null,
            label: LANE_LABELS[lane],
            tasks: all.filter((task) => task.lane === lane),
          },
        ],
      };
    }

    return {
      lane,
      groups: stages.map((stage) => ({
        id: groupId(lane, stage),
        lane,
        stage,
        label: STAGE_LABELS[stage],
        tasks: all.filter((task) => task.lane === lane && task.stage === stage),
      })),
    };
  });
}

/* -------------------------------------------------------------------------- */
/* The column                                                                 */
/* -------------------------------------------------------------------------- */

export interface BoardColumnProps {
  lane: Lane;
  groups: BoardGroup[];
  /** From `BoardDto.totals`. Absent only while the board is being rebuilt. */
  total?: { taskCount: number; points: number };
  canMove: boolean;
  onOpenTask: (task: TaskDto) => void;
  /** Stacked layouts give the column its own scroller; tabbed ones do not. */
  scrolls?: boolean;
  className?: string;
}

export function BoardColumn({
  lane,
  groups,
  total,
  canMove,
  onOpenTask,
  scrolls = false,
  className,
}: BoardColumnProps) {
  const grouped = groups.length > 1 || groups[0]?.stage !== null;
  const empty = groups.every((group) => group.tasks.length === 0);

  return (
    <section aria-label={LANE_LABELS[lane]} className={cn('flex min-w-0 flex-col', className)}>
      <LaneHeading lane={lane} count={total?.taskCount ?? 0} points={total?.points ?? 0} />

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col gap-3 pt-1',
          // A fixed-height scroller keeps the lane headings and the filters in
          // place while a long column is read. dnd-kit auto-scrolls it.
          scrolls && 'overflow-y-auto pr-1 xl:max-h-[calc(100dvh-19rem)]',
        )}
      >
        {/*
          A lane with stages keeps every group on screen even when the whole
          lane is empty, because each one is a drop target and hiding it would
          make Spec unreachable on an empty board. A lane without stages has
          nowhere to drop but itself, so it gets the full empty state.
        */}
        {groups.map((group) =>
          grouped ? (
            <div key={group.id} className="min-w-0">
              <h4 className="mb-1.5 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-muted">
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: group.stage ? stageColour(group.stage) : undefined }}
                  aria-hidden="true"
                />
                {group.label}
                <span className="ml-auto font-mono text-[0.6875rem] font-normal tracking-normal text-subtle tnum">
                  {group.tasks.length}
                </span>
              </h4>
              <DropList group={group} canMove={canMove} onOpenTask={onOpenTask} dense />
            </div>
          ) : (
            <DropList
              key={group.id}
              group={group}
              canMove={canMove}
              onOpenTask={onOpenTask}
              placeholder={
                empty ? (
                  <EmptyState
                    bare
                    title={`Nothing in ${LANE_LABELS[lane]}`}
                    description={
                      canMove
                        ? 'Drag a card here, or set the lane from the task itself.'
                        : 'Work appears here once it is planned.'
                    }
                  />
                ) : null
              }
            />
          ),
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The sortable list for one group.
 *
 * `SortableContext` scopes the keyboard and reorder behaviour to this group;
 * `useDroppable` on the same element is what makes an *empty* group a target,
 * since a SortableContext with no items has nothing to collide with.
 */
function DropList({
  group,
  canMove,
  onOpenTask,
  dense = false,
  placeholder = null,
}: {
  group: BoardGroup;
  canMove: boolean;
  onOpenTask: (task: TaskDto) => void;
  dense?: boolean;
  /** Shown instead of the one-line strip when the whole lane is empty. */
  placeholder?: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: group.id,
    data: { type: 'group', lane: group.lane, stage: group.stage },
  });

  return (
    <SortableContext
      id={group.id}
      items={group.tasks.map((task) => task.id)}
      strategy={verticalListSortingStrategy}
    >
      <ul
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 rounded-lg border border-transparent p-1 transition-colors duration-150',
          dense ? 'min-h-11' : 'min-h-24',
          isOver && canMove && 'border-dashed border-accent/50 bg-accent-soft/50',
        )}
      >
        {group.tasks.length === 0 ? (
          <li className="list-none">
            {placeholder ?? (
              <span className="grid place-items-center rounded-md border border-dashed border-line px-2 py-2.5 text-center text-[0.6875rem] text-subtle">
                {canMove ? 'Drop here' : 'Nothing here'}
              </span>
            )}
          </li>
        ) : (
          group.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              groupId={group.id}
              canMove={canMove}
              onOpen={onOpenTask}
            />
          ))
        )}
      </ul>
    </SortableContext>
  );
}
